import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import * as yup from "yup";
import {
  HeaderLogoImage,
  StyledContainer,
  StyledLogoContainer,
} from "./styles";
import LogoSvg from "src/images/logo.svg";
import { downloadCertificate } from "src/services/certificates";

const schema = yup.object({
  hash: yup
    .string()
    .required("Informe o código que está na etiqueta")
    .min(6, "Código inválido"),
});

export default function CertificateSearch() {
  const [hash, setHash] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const fetchCertificate = async () => {
    try {
      setFormError(null);
      await schema.validate({ hash });

      setLoading(true);
      setError(null);

      const response = await downloadCertificate(hash);
      if (!response) {
        throw new Error();
      }

      const url = URL.createObjectURL(response.data);
      setPdfUrl(url);
    } catch (err: any) {
      if (err.name === "ValidationError") {
        setFormError(err.message);
      } else {
        setError(
          "Certificado não encontrado. Verifique o código e tente novamente."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (pdfUrl) {
    return (
      <div style={{ width: "100%", height: "100vh" }}>
        <iframe
          src={pdfUrl}
          title="Certificado PDF"
          width="100%"
          height="100%"
          style={{ border: "none" }}
        />
      </div>
    );
  }

  return (
    <StyledContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: "100%", maxWidth: 420 }}
      >
        <Card elevation={6} sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={3}>
              <StyledLogoContainer>
                <HeaderLogoImage src={LogoSvg} alt="Logo Uniservice" />
              </StyledLogoContainer>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Consultar Certificado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Digite abaixo o código que está na etiqueta do seu equipamento.
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Código"
              placeholder="Ex: ABCDE123456"
              value={hash}
              error={!!formError}
              helperText={formError}
              disabled={loading}
              sx={{ mb: 2 }}
              onChange={(e) => {
                setHash(e.target.value);
                if (formError) setFormError(null);
                if (error) setError(null);
              }}
            />

            {error && (
              <Typography variant="body2" color="error" mb={2}>
                {error}
              </Typography>
            )}

            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={loading}
              onClick={fetchCertificate}
              sx={{ textTransform: "none", fontSize: 16, py: 1.3 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Consultar"
              )}
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </StyledContainer>
  );
}
